import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { from, switchMap } from 'rxjs';

import { AccessKeyProcessor } from '../../core/access-key-processor.service';
import { ApiEndpoints } from '../../core/api-endpoints.service';
import { CookieService } from '../../core/cookie.service';
import { IdService } from '../../core/id.service';
import { JoseService } from '../../core/jose.service';
import { StorageService } from '../../core/storage.service';
import { DataRecord, DEFAULT_LOCKED_SPACE } from '../../core/workflow-state.service';
import { ApiCallState } from '../../shared/api-call-state';
import {
  ApiEndpointComponent,
  ApiResultComponent,
} from '../../shared/api-result.component';
import { dateInputValue, mergeDateAndTime } from '../../shared/date-utils';

export type KeyTool = 'saved' | 'request-all' | 'retrieve-all';

interface SdkIdentity {
  id: string;
  key: string;
  value: string;
}

@Component({
  selector: 'app-key-management',
  imports: [FormsModule, ApiEndpointComponent, ApiResultComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './key-management.component.html',
})
export class KeyManagementComponent {
  readonly initialTool = input<KeyTool>('saved');
  readonly showNavigation = input(true);

  private readonly http = inject(HttpClient);
  private readonly endpoints = inject(ApiEndpoints);
  private readonly cookies = inject(CookieService);
  private readonly ids = inject(IdService);
  private readonly jose = inject(JoseService);
  private readonly processor = inject(AccessKeyProcessor);
  private readonly storage = inject(StorageService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly activeTool = signal<KeyTool>('saved');
  protected readonly call = new ApiCallState();
  protected readonly sdkIdentities = signal<SdkIdentity[]>([]);
  protected readonly toolTitle = computed(() => {
    switch (this.activeTool()) {
      case 'saved':
        return 'List of Saved Access Key';
      case 'request-all':
        return 'Open All Compartments';
      case 'retrieve-all':
        return 'Get All Compartment Keys';
    }
  });
  protected readonly endpoint = computed(() => {
    switch (this.activeTool()) {
      case 'saved':
        return `${this.endpoints.url(this.endpoints.accessKey)}/{requestId}`;
      case 'request-all':
        return this.endpoints.url(this.endpoints.openAllCompartments);
      case 'retrieve-all':
        return `${this.endpoints.url(this.endpoints.openAllCompartments)}/{requestId}`;
    }
  });

  protected savedRequestId = this.cookies.read('keyRequestId') ?? '';
  protected allKeysRequestId = this.cookies.read('allKeysRequestId') ?? '';
  protected lockId = '';
  protected hardwareId = '';
  protected provider = '';
  protected country = 'SE';
  protected requesterId = String(DEFAULT_LOCKED_SPACE['requesterId'] ?? 'ID1234');
  protected shipperName = String(DEFAULT_LOCKED_SPACE['shipperName'] ?? 'Postnord');
  protected keyUserId = String(DEFAULT_LOCKED_SPACE['keyUserId'] ?? 'PNDelPrsnl1234');
  protected ttlDate = futureDateInput(1);
  protected ttlTime = '23:59';
  protected readonly publicKey = this.jose.publicKey;
  protected readonly algorithm = this.jose.algorithm;
  protected readonly encryption = this.jose.encryption;

  constructor() {
    effect(() => this.openTool(this.initialTool()));
  }

  protected openTool(tool: KeyTool): void {
    this.activeTool.set(tool);
    this.call.clear();
    this.savedRequestId = this.cookies.read('keyRequestId') ?? this.savedRequestId;
    this.allKeysRequestId = this.cookies.read('allKeysRequestId') ?? this.allKeysRequestId;
  }

  protected regenerateAllKeysRequestId(): void {
    this.allKeysRequestId = this.ids.shortId();
  }

  protected addSdkIdentity(): void {
    this.sdkIdentities.update((items) => [
      ...items,
      { id: this.ids.uuid(), key: '', value: '' },
    ]);
  }

  protected removeSdkIdentity(id: string): void {
    this.sdkIdentities.update((items) => items.filter((item) => item.id !== id));
  }

  protected retrieveSavedKeys(): void {
    if (!this.savedRequestId) {
      return;
    }

    this.call.start({ requestId: this.savedRequestId });
    const cache = this.storage.get<Record<string, string>>('accessKeyCache', {});
    this.http
      .get<DataRecord>(
        `${this.endpoints.url(this.endpoints.accessKey)}/${encodeURIComponent(this.savedRequestId)}`,
      )
      .pipe(
        switchMap((response) => from(this.processor.process(response, new Map(), cache))),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (response) => this.call.succeed(response),
        error: (error: unknown) => this.call.fail(error),
      });
  }

  protected requestAllKeys(): void {
    if (!this.allKeysRequestId) {
      this.regenerateAllKeysRequestId();
    }
    const ttl = mergeDateAndTime(this.ttlDate, this.ttlTime);
    const payload = {
      requestor: {
        shipperName: this.shipperName,
        requesterId: this.requesterId,
      },
      ttl: Math.floor(ttl.getTime() / 1000),
      keyUserId: this.keyUserId,
      requestId: this.allKeysRequestId,
      lockId: this.lockId || undefined,
      hardwareId: this.hardwareId || undefined,
      provider: this.provider,
      country: this.country,
      encryption: {
        key: this.publicKey,
        alg: this.algorithm,
        enc: this.encryption,
        lockProviderSdkIdentities: this.sdkIdentities()
          .filter(({ key, value }) => key && value)
          .map(({ key, value }) => ({ [key]: value })),
      },
    };

    this.call.start(payload);
    this.http
      .post(this.endpoints.url(this.endpoints.openAllCompartments), payload, {
        observe: 'response',
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.cookies.write('allKeysRequestId', this.allKeysRequestId, 60);
          this.call.succeed({ status: response.status, body: response.body });
        },
        error: (error: unknown) => this.call.fail(error),
      });
  }

  protected retrieveAllKeys(): void {
    if (!this.allKeysRequestId) {
      return;
    }

    this.call.start({ requestId: this.allKeysRequestId });
    this.http
      .get<DataRecord>(
        `${this.endpoints.url(this.endpoints.openAllCompartments)}/${encodeURIComponent(this.allKeysRequestId)}`,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => this.call.succeed(response),
        error: (error: unknown) => this.call.fail(error),
      });
  }
}

function futureDateInput(days: number): string {
  const value = new Date();
  value.setDate(value.getDate() + days);
  return dateInputValue(value);
}